import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import type { GalleryItem } from "@/types/content";
import { gallery } from "@/mocks/gallery";
import { streamers } from "@/mocks/streamers";
import PhotoGallery from "@/components/feature/PhotoGallery";

const allGallery = gallery as GalleryItem[];
const THEMES = ["全部", "红毯", "日常", "广告拍摄"];
const PAGE_SIZE = 8;

const streamerNameOptions = ["全部主播", ...streamers.map((s) => s.name)];

export default function GalleryPage() {
  const [searchParams] = useSearchParams();
  const [streamerName, setStreamerName] = useState(searchParams.get("streamer") || "全部主播");
  const [theme, setTheme] = useState("全部");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setStreamerName(searchParams.get("streamer") || "全部主播");
  }, [searchParams]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [streamerName, theme]);

  const filtered = useMemo(() => {
    return allGallery
      .filter((g) => (streamerName === "全部主播" ? true : g.streamerName === streamerName))
      .filter((g) => (theme === "全部" ? true : g.theme === theme))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [streamerName, theme]);

  const visible = filtered.slice(0, visibleCount);

  const handleReset = () => {
    setStreamerName("全部主播");
    setTheme("全部");
  };

  return (
    <>
      <Seo
        title="韩国女主播写真图库 – 韩国女主播"
        description="浏览韩国女主播高清写真图库，涵盖红毯、日常与广告拍摄等主题，支持按主播与写真主题筛选，点击图片可放大并左右切换查看。"
        keywords="韩国女主播写真,主播图库,韩国主播照片,红毯写真,高清写真"
        canonical="/gallery"
      />

      <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: "写真图库" }]} />

        <header className="mt-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">写真图库</h1>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-3xl">
            精选韩国女主播高清写真，覆盖红毯、日常与广告拍摄等主题。支持按主播昵称与写真主题筛选，点击图片可放大查看并左右切换同一主播的其他图片。
          </p>
        </header>

        <div className="rounded-lg bg-background-50 border border-background-200/80 p-4 md:p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-xs font-medium text-foreground-500 sm:w-20 shrink-0">主播昵称</span>
            <div className="relative sm:w-64">
              <i className="ri-user-star-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <select
                value={streamerName}
                onChange={(e) => setStreamerName(e.target.value)}
                className="w-full h-10 pl-9 pr-8 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors cursor-pointer appearance-none"
              >
                {streamerNameOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-xs font-medium text-foreground-500 sm:w-20 shrink-0">写真主题</span>
            <div className="flex flex-wrap gap-2">
              {THEMES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTheme(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    theme === t ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-background-200/70">
            <p className="text-xs text-foreground-500">
              共 <span className="font-semibold text-accent-600">{filtered.length}</span> 张写真
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>

        <div className="mt-6">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-image-line text-3xl"></i>
              </span>
              <h3 className="font-heading font-semibold text-foreground-900 mb-2">没有找到匹配的写真</h3>
              <p className="text-sm text-foreground-500 mb-5">换个主播或主题试试吧</p>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <PhotoGallery items={visible} columns="dense" />
              {visibleCount < filtered.length && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    加载更多（剩余 {filtered.length - visibleCount} 张）
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}